import { Color, Spacing } from '@persona-id/design-tokens';
import {
  BriefcaseIcon,
  CalendarIcon,
  ChatIcon,
  ClockIcon,
  LoaderIcon,
  MailIcon,
} from '@persona-id/design-tokens/icons';
import dompurify from 'dompurify';
import { DateTime } from 'luxon';
import { ReactNode, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';

import { UserNotification, UserNotificationsCategories } from 'dashboard/models/user-notification';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import FloatingTooltip from 'design-system/components/Tooltip';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import { PaginationParams } from 'lib/interfaces/api';

const NOTIFICATION_ICON: Partial<Record<UserNotificationsCategories, ReactNode>> = {
  'case.assignment': <BriefcaseIcon color={Color.BorderIconActive} />,
  'case.mention': <ChatIcon color={Color.BorderIconActive} />,
  'case.creation': <MailIcon color={Color.BorderIconActive} />,
  'case.message_received': <MailIcon color={Color.BorderIconActive} />,
  'case.sla': <ClockIcon color={Color.BorderIconActive} />,
};

const NotificationItemContainer = styled('div')`
  padding: 10px ${Spacing[3]};

  display: grid;
  grid-template-columns: 32px auto auto;
  align-items: center;
  grid-column-gap: ${Spacing[2]};

  align-items: start;

  border-bottom: 1px solid ${Color.BorderHairline};

  &:last-child {
    border-bottom: none;
  }
`;

const IconContainer = styled('div')`
  height: 32px;
  width: 32px;
  background-color: ${Color.CorePurple100};
  border-radius: ${({ theme }) => theme.element.borderRadius};

  display: flex;
  align-items: center;
  justify-content: center;
`;

const MarkAsReadButton = styled(Button)`
  justify-self: right;
`;

const NotificationLoaderIcon = styled(LoaderIcon)`
  display: block;
  margin: ${Spacing[2]} auto;
`;

export interface UserNotificationsListProps {
  containerId: string;
  userNotifications: UserNotification[];
  userNotificationsPaginationParams: PaginationParams | undefined;
  nullState: ReactNode;
  actionIcon?: ReactNode;
  actionTooltipText?: string;
  loadUserNotificationPage: (pageNumber: number) => void;
  handleAction?: (notificationId: string) => void;
}

const UserNotificationsList = ({
  containerId,
  userNotifications: unsortedUserNotifications,
  userNotificationsPaginationParams,
  nullState,
  actionIcon,
  actionTooltipText,
  loadUserNotificationPage,
  handleAction,
}: UserNotificationsListProps) => {
  const currentNotificationPage = userNotificationsPaginationParams?.['current-page'] ?? 0;
  const totalNotificationPages = userNotificationsPaginationParams?.['total-pages'] ?? 0;

  const userNotifications = useMemo(() => {
    return [...unsortedUserNotifications].sort((a, b) =>
      a.attributes['delivered-at'] < b.attributes['delivered-at'] ? 1 : -1,
    );
  }, [unsortedUserNotifications]);

  const notificationItems = userNotifications.map((notif: UserNotification) => {
    const icon = NOTIFICATION_ICON[notif.attributes.category] ?? <CalendarIcon size={24} />;
    const deliveredAtDisplay = DateTime.fromISO(notif.attributes['delivered-at']).toFormat(
      DATE_AND_TIME_FORMAT,
    );

    return (
      <NotificationItemContainer
        data-test={`user-notifications__notification-item--${notif.id}`}
        key={notif.id}
      >
        <IconContainer>{icon}</IconContainer>
        <div>
          <Paragraph
            size='sm'
            color='primary'
            dangerouslySetInnerHTML={{
              __html: dompurify.sanitize(notif.attributes.subject, {
                ALLOWED_TAGS: ['a'],
              }),
            }}
          />
          <Paragraph size='sm'>{deliveredAtDisplay}</Paragraph>
        </div>
        {actionIcon && handleAction && (
          <>
            {actionTooltipText ? (
              <FloatingTooltip
                side='top'
                zIndex={Z_INDICES.topNavTooltip}
                trigger={
                  <MarkAsReadButton
                    id={`user-notifications__action--${notif.id}`}
                    data-test={`user-notifications__action--${notif.id}`}
                    variant='basic'
                    width='icon'
                    autoFocus={false}
                    onClick={() => handleAction(notif.id)}
                  >
                    {actionIcon}
                  </MarkAsReadButton>
                }
              >
                <Paragraph color='primary'>{actionTooltipText}</Paragraph>
              </FloatingTooltip>
            ) : (
              <MarkAsReadButton
                id={`user-notifications__action--${notif.id}`}
                data-test={`user-notifications__action--${notif.id}`}
                variant='basic'
                width='icon'
                autoFocus={false}
                onClick={() => handleAction(notif.id)}
              >
                {actionIcon}
              </MarkAsReadButton>
            )}
          </>
        )}
      </NotificationItemContainer>
    );
  });

  // NOTE: (melissa) Truly, I do not know why we need `dataLength` and `hasMore`, but here we are.
  // We know if we have more notifications to load if the total number of pages is greater than the current
  // page, and so if we have more notifications to load, we set the data length to be current number
  // of loaded notifications + 1 so that InfiniteScroll understands that there are more items to be loaded.
  const hasMore = totalNotificationPages > currentNotificationPage;
  const dataLength = hasMore ? userNotifications.length + 1 : userNotifications.length;

  return (
    <>
      {notificationItems.length > 0 ? (
        <InfiniteScroll
          dataLength={dataLength}
          next={() => loadUserNotificationPage(currentNotificationPage + 1)}
          hasMore={hasMore}
          loader={<NotificationLoaderIcon />}
          scrollableTarget={containerId}
        >
          {notificationItems}
        </InfiniteScroll>
      ) : (
        nullState
      )}
    </>
  );
};

export default UserNotificationsList;
